<script lang="ts" setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'

import { useSessionStore } from '../../stores/session'
import { useConfigStore } from '../../stores/config'
import EventBus from '../../utils/event-bus'

const props = withDefaults(
  defineProps<{
    metaData: object,
    pageData: object,
    pageMeta: object,
    brand: string
  }>(), {
    metaData: () => ({}),
    pageData: () => ({}),
    pageMeta: () => ({}),
    brand: ''
  }
)

const nSdkInstance = ref(null)
const latestAgfMetaData = ref({})

const configStore = useConfigStore()
const sessionStore = useSessionStore()
const rsConfig = computed(() => configStore.rsConfig)

const agfConfig = computed(() => rsConfig.value.agfConfig)

const isAgfConfigValid = computed(() => !!agfConfig.value.vcid.trim() &&
  Object.values(agfConfig.value.appId).every(value => value.trim() !== '') &&
  !!agfConfig.value.segAPrefix.trim())

const simulateContentPassOrContentPass = computed(() => sessionStore.simulateContentPassOrContentPass)

const isScriptLoaded = ref(false)

const cleanupAGF = () => {
  // Disable and remove script from DOM
  const script = document.querySelector('script[data-vmid="agf-static-queue-snippet"]') as HTMLScriptElement
  if (script) {
    script.text = '' // Disable the script by emptying its content
    script.remove()
  }

  // Clean up NOLBUNDLE
  if (window.NOLBUNDLE) {
    nSdkInstance.value = null
    window.NOLBUNDLE = null
  }

  isScriptLoaded.value = false
}

const loadOrRemoveAgfScript = () => {
  // Check all conditions
  const shouldLoad = agfConfig.value.enabled &&
    isAgfConfigValid.value &&
    simulateContentPassOrContentPass.value === false &&
    process.env.NODE_ENV !== 'development'

  // Check if script already exists
  const existingScript = document.querySelector('script[data-vmid="agf-static-queue-snippet"]')
  if (existingScript) {
    if (!shouldLoad) {
      cleanupAGF()
    }
    return
  }

  // If script should be loaded but isn't loaded yet
  if (shouldLoad && !isScriptLoaded.value) {
    isScriptLoaded.value = true

    // Insert script directly into DOM
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.text = AGF_SCRIPT
    script.setAttribute('data-vmid', 'agf-static-queue-snippet')
    script.async = false
    document.head.appendChild(script)

    // Wait a moment for the script to be executed
    requestAnimationFrame(() => {
      initializeNOLBUNDLE()
    })
  }
}

// Watch for changes in conditions
watch([simulateContentPassOrContentPass, agfConfig], () => {
  loadOrRemoveAgfScript()
}, { deep: true })

const AGF_SCRIPT = `
  !(function (e, n) {
    function t (e) {
      return typeof e === 'object' ? JSON.parse(JSON.stringify(e)) : e
    }
    e[n] = e[n] || {
      nlsQ: function (o, r, c) {
        const s = e.document
        const a = s.createElement('script')
        a.async = 1,
        a.src = (e.location.protocol === 'http:' ? 'http:' : 'https:') + '//cdn-gl.nmrodam.com/conf/' + o + '.js#name=' + r + '&ns=' + n
        const i = s.getElementsByTagName('script')[0]
        return i.parentNode.insertBefore(a, i),
        e[n][r] = e[n][r] || {
          g: c || {},
          ggPM: function (o, c, s, a, i) { e[n][r].q = e[n][r].q || []; try { const l = t([o, c, s, a, i]); e[n][r].q.push(l) } catch (e) { console && console.log && console.log('Error: Cannot register event in Nielsen SDK queue.') } },
          trackEvent: function (o) { e[n][r].te = e[n][r].te || []; try { const c = t(o); e[n][r].te.push(c) } catch (e) { console && console.log && console.log('Error: Cannot register event in Nielsen SDK queue.') } }
        },
        e[n][r]
      }
    }
  }(window, 'NOLBUNDLE'))`

const section = computed(() => {
  const path = props.pageData?.mainStructurePath
    ?.slice(1)
    .map(item => item?.url?.replace('/', ''))
    .filter(url => url && url !== '')
    .join('_')
  return props.brand + (path ? `_${path}` : '')
})

const metaDataTag = computed(() => (props.metaData?.IVWTag || props.metaData?.AGFTag || ''))
const segA = computed(() => agfConfig.value.segAPrefix + '_' + metaDataTag.value)

const agfMetaData = (segB = 'CPI') => {
  if (!props.pageMeta) {
    return
  }

  latestAgfMetaData.value = {
    type: 'static',
    assetid: props.pageMeta?.id,
    section: section.value,
    segA: segA.value,
    segB,
    segC: 'UNKNOWN',
    subbrand: agfConfig.value.vcid
  }
  window.localStorage.setItem('agfMetaData', JSON.stringify(latestAgfMetaData.value))
  return latestAgfMetaData.value
}

const triggerAdvertisingMedium = () => {
  if (isScriptLoaded.value) {
    nSdkInstance.value.ggPM('staticend', latestAgfMetaData.value)
    nSdkInstance.value.ggPM('staticstart', agfMetaData('WPI'))
  }
}

const backForwardCacheHandler = () => {
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      latestAgfMetaData.value = JSON.parse(window.localStorage.getItem('agfMetaData'))
      nSdkInstance.value.ggPM('staticend', latestAgfMetaData.value)
      nSdkInstance.value.ggPM('staticstart', agfMetaData())
    }
  })
}

const initializeNOLBUNDLE = () => {
  const enableDebugging = false
  const debugOptions = enableDebugging && ['development', 'testing'].includes(process.env.NODE_ENV) ? { nol_sdkDebug: 'debug' } : {}

  const initNOLBUNDLE = () => {
    nSdkInstance.value = window.NOLBUNDLE.nlsQ(rsConfig.value.agfConfig.appId.static, 'nlsnInstance', debugOptions)
    nSdkInstance.value?.ggPM('staticstart', agfMetaData())
    backForwardCacheHandler()
  }

  if (window.NOLBUNDLE) {
    initNOLBUNDLE()
  } else {
    // Wait for NOLBUNDLE with Promise and AbortController
    const waitForNOLBUNDLE = () => {
      const controller = new AbortController()

      const observerPromise = new Promise<void>((resolve) => {
        if (window.NOLBUNDLE) {
          resolve()
        } else {
          const observer = new MutationObserver(() => {
            if (window.NOLBUNDLE) {
              observer.disconnect()
              resolve()
            }
          })

          observer.observe(document.documentElement, {
            childList: true,
            subtree: true
          })

          controller.signal.addEventListener('abort', () => {
            observer.disconnect()
            resolve()
          })
        }
      })

      const timeoutPromise = new Promise<void>((resolve) => {
        controller.signal.addEventListener('abort', () => resolve())
      })

      requestAnimationFrame(() => {
        controller.abort()
      })

      return Promise.race([observerPromise, timeoutPromise])
    }

    waitForNOLBUNDLE().then(() => {
      if (window.NOLBUNDLE) {
        initNOLBUNDLE()
      }
    })
  }
}

// Initialize AGF script
onMounted(() => {
  loadOrRemoveAgfScript()

  EventBus.$on('triggerAGF', triggerAdvertisingMedium)
})

onBeforeUnmount(() => {
  EventBus.$off('triggerAGF', triggerAdvertisingMedium)
  if (isScriptLoaded.value) {
    const script = document.querySelector('script[data-vmid="agf-static-queue-snippet"]')
    if (script) {
      script.remove()
    }
    isScriptLoaded.value = false
  }
})
</script>

<template>
  <span />
</template>
